/* text_editor.control_adapter.js */

"use strict";
const $ = require("jquery");
const _ = require("lodash");
const underscoreString = require("underscore.string");
_.mixin(underscoreString.exports());
const EventEmitter = require("events").EventEmitter;
const q = require("q");
//const aceSearch = require('brace/ext/searchbox');
const StyleUtil = require("../StyleUtil");
const TextEditorComponent = require("@gluenetworks/text_editor").TextEditorComponent;

/**
 * @event TextEditorAdapter#ValueChanged
 * @type {Object}
 * @property oldValue {string} The previous value
 * @property newValue {string} The new value
 */
/**
 * @event TextEditorAdapter#InitializationComplete
 * @description This event is fired when the control has completely Initialized.
 * @type {Object}
 * @property initializedValue {string} The initialized "starting" value
 */

/**
 * @class TextEditorAdapter
 * @classdesc exposes the ace editor as a Form Control.
 * @implements AngularControlAdapter
 *
 * @param logger {Bunyan} The logger
 * @param containingElem {DomElement} The containing dom element for this control
 * @param formCtrlDef {FormControlDefinition} The definition of this control object.
 *
 * @requires EventEmitter
 *
 * @fires TextEditorAdapter#ValueChanged
 */
class TextEditorAdapter extends EventEmitter {
    /*
     * Tell the Form renderer that this is an Angular Control.
     * @returns {boolean}
     */
    static get isAngularControl() {
        return true;
    }

    /**
     * Tell the Form renderer that this adapter supports multi edit mode.
     * @returns {boolean}
     */
    static get isMultiEditSupported() {
        return true;
    }

    constructor(logger, containingElem, formCtrlDef) {
        super();

        this._multiEditMode = _.get(formCtrlDef, "displayModeOptions.multiEditMode", false);
        this.containingElem = containingElem;
        this.formCtrlDef = formCtrlDef;
        this.logger = logger;

        //The name of the control
        this.name = this.formCtrlDef.name;
        this.oldVal = "";
        this._required = false;
        this._oldInvalidStatus = false;
        this._validationMessages = [];
        /**
         * @type {Array.<String>}
         * @description $inject Annotation By adding the $inject property onto the init function the Angular
         * injection parameters can be specified.
         */
        this.$init.$inject = ["$scope", "$templateCache", "$timeout"];
        this.$compiledAndLinked.$inject = ["$scope", "$timeout"];
    }

    _setInitialReadOnlyState() {
        if (this._readOnlyField) {
            this.setReadOnly(true);
        }
        if (this._multiEditMode) {
            this.setReadOnly(true);
        }
    }

    /**
     * @returns {string|Array} the data associated with this control.
     */
    getValue() {
        let val = this._textEditorComponent.getValue();
        if (_.get(this.formCtrlDef, "configSettings.fieldType") === "Array") {
            val = _.isEmpty(val) ? [] : val.split(/\r?\n/);
        }
        return val;
    }

    /**
     * Set this controls current value.
     * @param val {string|Array} The value to set this control to.
     * @returns {string|Array} the value associated with this control, it might not always be the same as the value passed in for
     * example adapters with a ValueScope.PARENT.
     */
    setValue(val) {
        let result = val;
        if (Array.isArray(val)) {
            result = val
                .map((element) => {
                    return _.isObject(element) ? element.value : element;
                })
                .join("\n");
        }
        this._textEditorComponent.setValue(result);
    }

    getMultiEditActive() {
        return this._multiEditActive;
    }

    setMultiEditActive(flag) {
        if (!this._readOnlyField) {
            this.setReadOnly(!flag);
            let oldValue = this._multiEditActive;
            this._multiEditActive = flag;
            this._updateValidityStatus();
            if (oldValue !== this._multiEditActive) {
                this.emit("MultiEditActive", {
                    oldValue: oldValue,
                    newValue: this._multiEditActive,
                });
            }
        }
    }

    setReadOnly(flag) {
        this._textEditorComponent.setDisabled(flag);
    }

    /**
     * Returns if the editor has invalid value
     * @returns {boolean}
     */
    isInvalid() {
        let readOnlyInMultiEdit = this._multiEditMode && !this.getMultiEditActive();
        if (readOnlyInMultiEdit || this._readOnlyField) {
            return false;
        }
        return this._textEditorComponent.isInvalid();
    }

    /**
     * Returns validation messages if value of editor is invalid
     * @returns {Array}
     */
    getValidationMessages() {
        return this._textEditorComponent.getValidationMessages();
    }

    /**
     * Tear down this control
     */
    tearDown() {
        this._textEditorComponent.shutdown();
    }

    processDisplayProps() {
        let options = {};
        if (_.has(this.formCtrlDef, "configSettings.Display.editor")) {
            let editorOptions = _.get(this.formCtrlDef, "configSettings.Display.editor");
            options = {
                minLineCount: 10,
                maxLineCount: Infinity,
                showLineNumbers: true,
                showHiddenText: false,
                tabSize: 4,
                languageMode: "ace/mode/text",
                theme: "ace/theme/chrome",
            };
            options = _.merge(options, editorOptions);
        }
        return options;
    }

    showSearch() {
        if (this.editor.searchBox) {
            this.editor.searchBox.show();
        } else {
            ace.config.loadModule("ace/ext/searchbox", (e) => {
                e.Search(this.editor);
            });
        }
    }

    showReplace() {
        if (this.editor.searchBox) {
            this.editor.searchBox.show(null, true);
        } else {
            ace.config.loadModule("ace/ext/searchbox", (e) => {
                e.Search(this.editor);
            });
            setTimeout(() => {
                if (this.editor.searchBox) {
                    this.editor.searchBox.show(null, true);
                }
            }, 100);
        }
    }

    /**
     * A Hook to start the angular work, due to timing issues don't try anything angularis before this hook is called.
     * @param $scope
     * @param $templateCache
     */
    $init($scope, $templateCache /*, $timeout */) {
        let configOptions = this.processDisplayProps();
        this._textEditorComponent = new TextEditorComponent(configOptions);
        let container = this._textEditorComponent.renderer.getRootElement();
        container.classList.add("text_editor_wrapper");
        let psuedoUUID = function () {
            return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
                let r = (Math.random() * 16) | 0,
                    v = c === "x" ? r : (r & 0x3) | 0x8;
                return v.toString(16);
            });
        };
        this.containerId = psuedoUUID();
        container.id = this.containerId;
        this.editorContainer = container.getElementsByClassName("text-editor")[0];
        this.editorContainer.classList.add("text_editor_container");
        this.editorErrorsDiv = container.getElementsByClassName("errors")[0];
        this.editorErrorsDiv.classList.add("text_editor_errors");
        this.containingElem.append(container);

        let stylesConfig = _.get(this.formCtrlDef, "configSettings.Display.styles", {});
        StyleUtil.renderStylesConfig(stylesConfig, this.containingElem[0]);

        // Apply item layout styles - these styles customize how this element is placed inside parent element
        let itemLayoutCssClass = _.get($scope, "jTypeContext.layout.item.cssClass", "");
        this.containingElem.addClass(itemLayoutCssClass);

        StyleUtil.appendClassListToElement(
            $scope.jTypeContext.layout.inlineLabels,
            this.containingElem[0]
        );

        this._textEditorComponent.on("UserValue", () => {
            let newVal = this._textEditorComponent.getValue();
            let oldVal = this.oldValue;
            if (newVal !== oldVal) {
                this.emit("ValueChanged", {
                    name: this.formCtrlDef.name,
                    containingElem: this.containingElem,
                    controlAdapter: this,
                    formControlDefinition: this.formCtrlDef,
                    oldValue: oldVal,
                    newValue: newVal,
                });
                this.oldValue = newVal;
            }
            this._updateValidityStatus();
        });
        this._scope = $scope;
        $scope.formCtrlDef = this.formCtrlDef;

        //Notify the world this adapter is ready to be compiled/linked and values retrieved
        this.emit("InitializationComplete", {
            initializedValue: this.getValue(),
        });

        this.setValue(this.formCtrlDef.value);
        this.loadDeferred = q.defer();
        return this.loadDeferred.promise;
    }

    /**
     * A Hook for when this control has been compiled/Linked.
     * @param $scope {$scope} The Angular Scope variable
     * @param $timeout
     *
     * if $compiledAndLinked.$inject is specified those injection parameters will be used as the arguments
     */
    $compiledAndLinked($scope, $timeout) {
        if (_.get(this.formCtrlDef, "configSettings.Display.readOnly")) {
            this._readOnlyField = true;
        }
        this._required = _.get(this.formCtrlDef, "validationInfo.required", false);
        this._textEditorComponent.setRequired(this._required);

        // Allow the DOM to update before appending the context menus.
        $timeout(() => {
            // On the next cycle let the parents know the form is rendered.
            $timeout(() => {
                this._setInitialReadOnlyState();
                this._updateValidityStatus();
                this.loadDeferred.resolve();
            });
        });

        // Put this on a separate timeout because the
        // ACE editor takes a while to render and we want to make sure the form sends
        // a finish ASAP
        // $timeout(() => {
        //     this.editor.searchBox.hide();
        // });
    }

    _updateValidityStatus() {
        let newInvalidStatus = this.isInvalid();
        if (newInvalidStatus !== this._oldInvalidStatus) {
            this.emit("Invalid", {
                name: this.formCtrlDef.name,
                containingElem: this.containingElem,
                controlAdapter: this,
                formControlDefinition: this.formCtrlDef,
                oldValue: this._oldInvalidStatus,
                newValue: newInvalidStatus,
            });
            this._oldInvalidStatus = newInvalidStatus;
        }
    }
}

module.exports = TextEditorAdapter;
